import React, { Dispatch, SetStateAction } from "react";

import { MessageSquare, Mic, X } from "lucide-react";

import { EConversationMode } from "../../../types/entities.enum";

interface IConversationModePickerProps {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
    isProcessing: boolean;
    status: 'not-started' | 'in-progress' | 'completed' | null;
    restart: boolean;
    onSelectMode: (mode: EConversationMode) => void;
    apiError: string | null;
    setApiError: Dispatch<SetStateAction<string | null>>;
};

export const ConversationModePicker = (props: IConversationModePickerProps) => {
    const { visible, setVisible, isProcessing, status, restart, onSelectMode, apiError, setApiError } = props;

    const statusLabel = restart
        ? 'Restart'
        : status === 'not-started'
            ? 'Start'
            : status === 'in-progress'
                ? 'Continue'
                : 'Retry';

    return (
        <div className={`mbts_conversation_mode_picker ${visible ? 'mbts_conversation_mode_picker--visible' : ''}`}>
            <div className='card card--md card--radius-md mbts_conversation_mode_picker__content'>
                <button
                    type='button'
                    className='mbts_conversation_mode_picker__close'
                    onClick={() => {
                        setVisible(false);
                        setApiError(null);
                    }}
                    disabled={isProcessing}
                    aria-label='Close'
                >
                    <X />
                </button>

                <p className='text-2xl text-bold color--primary text-center mb-md'>
                    Choose how to {statusLabel.toLowerCase()}
                </p>
                <p className='color--txt-muted text-center mb-2xl'>
                    Pick a conversation mode. You can use text chat or talk with voice.
                </p>

                <div className='mbts_conversation_mode_picker__options'>
                    <button
                        type='button'
                        className='mbts_conversation_mode_picker__option'
                        onClick={() => onSelectMode(EConversationMode.CHAT)}
                        disabled={isProcessing}
                    >
                        <span className='mbts_conversation_mode_picker__option__icon'>
                            <MessageSquare />
                        </span>
                        <span className='text-lg text-semibold'>Chat</span>
                        <span className='text-sm color--txt-muted text-center mbts_conversation_mode_picker__option__desc'>
                            Type your responses in a text conversation
                        </span>
                    </button>

                    <button
                        type='button'
                        className='mbts_conversation_mode_picker__option'
                        onClick={() => onSelectMode(EConversationMode.AUDIO)}
                        disabled={isProcessing}
                    >
                        <span className='mbts_conversation_mode_picker__option__icon'>
                            <Mic />
                        </span>
                        <span className='text-lg text-semibold'>Voice</span>
                        <span className='text-sm color--txt-muted text-center mbts_conversation_mode_picker__option__desc'>
                            Speak with the persona using voice chat
                        </span>
                    </button>
                </div>

                {!!apiError && (
                    <p className='text-sm color--error text-center mt-lg'>{apiError}</p>
                )}

                {isProcessing && !apiError && (
                    <div className='d-flex align-items-center justify-content-center gap-sm mt-lg'>
                        <span className='btn-loader' />
                        <p className='text-sm color--txt-secondary'>Starting conversation...</p>
                    </div>
                )}
            </div>
        </div>
    );
};
